import React from 'react'
import { toast } from 'react-toastify'

import Button from 'components/base/Button'
import ButtonRow from 'components/base/Button/Row'
import Checkbox from 'components/base/Form/Input/Checkbox'
import Content from 'components/base/Content'
import InputGroup from 'components/base/Form/InputGroup'
import { Type as InputType } from 'components/base/Form/Input/types'
import Label from 'components/base/Form/Label'
import Link from 'components/base/Link'
import Option from 'components/base/Form/Input/Select/Option'
import Paragraph, { Theme as ParagraphTheme } from 'components/base/Paragraph'
import Text from 'components/base/Text'
import TextArea from 'components/base/Form/Input/TextArea'
import Title from 'components/base/Title'

import ConciergeIcon from 'components/blocks/ConciergeIcon'

import { Props } from './types'
import { CheckboxWithDescription } from './styles'

const EmailForm: React.FC<Props> = ({
  comments,
  consentToMarketingMessages,
  description = <>Reach out and an Insurance Concierge<sup>®</sup> will get in touch!</>,
  emailAddress,
  isSubmitSuccess,
  isSubmitting,
  isTopicForced,
  name,
  onSubmit,
  phoneNumber,
  setComments,
  setConsentToMarketingMessages,
  setEmailAddress,
  setName,
  setPhoneNumber,
  setStatesInteresetedIn,
  setTopic,
  showConcierge = true,
  showConsentToMarketingMessages,
  showPhoneNumber,
  showStatesInteresetedIn,
  statesInteresetedIn,
  submitButtonText = 'Send Question',
  successDescription = <>An Insurance Concierge<sup>®</sup> will be in touch with you shortly.</>,
  successTitle = 'Thank you for your inquiry.',
  title = 'Ask A Question',
  topic,
}): React.ReactElement => (
  <>
    <Content short={true} center={true} margined={true}>
      {(isSubmitSuccess && (
        <Title level={2}>{successTitle}</Title>
      )) || (
        <Title level={2}>{title}</Title>
      )}
      {(showConcierge && <ConciergeIcon title='Hi!' />) || null}
    </Content>

    <Content short={true} center={true} margined={true}>
      {(isSubmitSuccess && (
        <Paragraph theme={ParagraphTheme.large}>
          {successDescription}
        </Paragraph>
      )) || (
        <Paragraph theme={ParagraphTheme.large}>
          {description}
        </Paragraph>
      )}
    </Content>

    {(!isSubmitSuccess && (
      <Content short={true} margined={true}>
        <InputGroup
          type={InputType.text}
          value={name}
          onChange={(value): void => setName(value)}
          label='First and Last Name'
        />
        <InputGroup
          type={InputType.email}
          value={emailAddress}
          onChange={(value): void => setEmailAddress(value)}
          label='Email Address'
        />
        {(showPhoneNumber && (
          <InputGroup
            type={InputType.tel}
            value={phoneNumber}
            onChange={(value): void => setPhoneNumber(value)}
            label='Phone Number'
          />
        ))}
        {(showStatesInteresetedIn && (
          <InputGroup
            type={InputType.text}
            value={statesInteresetedIn}
            onChange={(value): void => setStatesInteresetedIn(value)}
            label='What states are you interested in?'
          />
        ))}
        {(!isTopicForced && (
          <InputGroup
            type={InputType.select}
            value={topic}
            onChange={(value): void => setTopic(value)}
            label='Topic Area'
          >
            <Option />
            <Option>Existing Policy Holder with Questions</Option>
            <Option>Finding New Coverage</Option>
            <Option>General Insurance Questions</Option>
            <Option>Billing and Payment Questions</Option>
            <Option>Franchising</Option>
            <Option>Other</Option>
          </InputGroup>
        )) || null}
        <InputGroup>
          <Label htmlFor='comments'>Comments</Label>
          <TextArea
            onChange={(event): void => setComments(event.target.value)}
            id='comments'
            valid={true} // prevent orange outline
          >
            {comments as string}
          </TextArea>
        </InputGroup>
        {(showConsentToMarketingMessages && (
          <CheckboxWithDescription>
            <Checkbox
              aria-label='I consent to joining the email list and receiving SMS from Insurance Lounge, with access to our latest offers and services. Message and data rates may apply. Message frequency varies. More details on this are in our Privacy Policy and Terms and Conditions. Feel free to contact us with any questions at 1-800-211-8660. Text &quot;STOP&ldquo; to cancel.'
              name='consentToMarketingMessages'
              value={consentToMarketingMessages}
              onChange={event => setConsentToMarketingMessages(event.target.checked)}
            />
            <Text>
              I consent to joining the email list and receiving SMS from Insurance Lounge, with access to our latest offers and services. Message and data rates may apply. Message frequency varies. More details on this are in our <Link to='/privacy' isExternal={true}>Privacy Policy</Link> and <Link to='/terms' isExternal={true}>Terms and Conditions</Link>. Feel free to contact us with any questions at 1-800-211-8660. Text &quot;STOP&ldquo; to cancel.
            </Text>
          </CheckboxWithDescription>
        )) || null}
        <ButtonRow>
          <Button
            center={true}
            disabled={isSubmitting}
            onClick={(event: React.MouseEvent): void => {
              event.preventDefault()

              onSubmit().catch((errorMessage: string) => toast(errorMessage || 'An error has occurred. Please try again.'))
            }}
          >
            {isSubmitting ? 'Sending...' : submitButtonText}
          </Button>
        </ButtonRow>
      </Content>
    ))}
  </>
)

export * from './types'

export default EmailForm
